import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useAuth } from "../../context/AuthContext";
import { useUserProgress } from "../../lib/gamification";

// Module ↔ pack-* mapping. Used to recommend the next course based on
// which pack badges the user hasn't earned. Kept inline to avoid pulling
// elearning-modules.json into this lightweight modal.
const PACK_ORDER = [
  { id: "pack-linux", category: "Linux", moduleSlug: "linux-networking-foundations", moduleTitle: "Linux and Networking Foundations" },
  { id: "pack-containers", category: "Containers", moduleSlug: null, moduleTitle: null },
  { id: "pack-kubernetes", category: "Kubernetes", moduleSlug: "k8s-for-backend-engineers", moduleTitle: "Kubernetes for Backend Engineers" },
  { id: "pack-cicd", category: "CI/CD", moduleSlug: "cicd-pipelines", moduleTitle: "CI/CD Pipelines that Don't Break" },
  { id: "pack-iac", category: "IaC", moduleSlug: "terraform-zero-to-prod", moduleTitle: "Terraform from Zero to Production" },
  { id: "pack-observability", category: "Observability", moduleSlug: "observability-prom-grafana", moduleTitle: "Observability with Prometheus and Grafana" },
];

// Bonus modules to suggest once every pack is earned.
const BONUS_MODULES = [
  { slug: "ai-engineer", title: "AI Engineering for DevOps" },
  { slug: "aws-cost-optimization", title: "AWS Cost Optimization" },
];

const recommendNext = (badgeIds = []) => {
  const earned = new Set(badgeIds);
  const nextPack = PACK_ORDER.find((p) => !earned.has(p.id));
  if (nextPack) {
    if (nextPack.moduleSlug) {
      return {
        kind: "course",
        title: nextPack.moduleTitle,
        href: `/academy/elearning/${nextPack.moduleSlug}/`,
        why: `unlock the ${nextPack.category} pack`,
      };
    }
    return {
      kind: "labs",
      title: `${nextPack.category} labs`,
      href: "/labs",
      why: `complete the ${nextPack.category} pack — no course yet, jump straight to the challenges`,
    };
  }
  // All packs earned — suggest a bonus module the user likely hasn't seen.
  const bonus = BONUS_MODULES[0];
  if (!bonus) {
    return {
      kind: "done",
      title: "You're caught up.",
      href: "/academy",
      why: "every pack and bonus module is done. Browse the catalog for what's new",
    };
  }
  return {
    kind: "bonus",
    title: bonus.title,
    href: `/academy/elearning/${bonus.slug}/`,
    why: "every pack is yours. Pick up a bonus module to keep the streak",
  };
};

// Subscribed via wrapRootElement in gatsby-browser.js. Open via:
//   window.dispatchEvent(new CustomEvent("gami:open-stats"))
// XpBadge + StreakFlame fire it on click.

// Mechanical, slightly dry. One per day, picked from a stable rotation
// so the same user sees a different line each day without server state.
const PHRASES = [
  "Make it work. Make it right. Make it fast.",
  "The hard part isn't the code. It's deciding what not to build.",
  "Production is where assumptions go to die.",
  "Every CI minute is a minute of someone's life.",
  "Yesterday's heroics are today's tech debt.",
  "If you can't explain it, you don't understand it.",
  "The best monitoring is a customer who never calls.",
  "Ship small. Ship often. Ship boring.",
  "A solved problem is a problem you can forget.",
  "Configuration is code. Treat it like code.",
  "Cache invalidation is hard. Naming is harder.",
  "Boring tech is a competitive advantage.",
  "Logs are for the future you who forgot.",
  "Backups you haven't restored aren't backups.",
];

const pickPhrase = (uid) => {
  const day = Math.floor(Date.now() / 86_400_000);
  let seed = day;
  if (uid) {
    for (let i = 0; i < uid.length; i += 1) seed = (seed * 31 + uid.charCodeAt(i)) >>> 0;
  }
  return PHRASES[seed % PHRASES.length];
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.78);
  /* Sits above XpToast (z-index 9999) so an award fired while the
     modal is open doesn't paint the toast over the avatar/stats. */
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const Panel = styled.div`
  background: var(--nd-black);
  border: 1px solid var(--nd-border-visible);
  border-radius: 12px;
  padding: 32px;
  max-width: 480px;
  width: 100%;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const Kicker = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
`;

const CloseBtn = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: transparent;
  color: var(--nd-text-secondary);
  border: 1px solid var(--nd-border-visible);
  border-radius: 999px;
  padding: 4px 12px;
  cursor: pointer;
  &:hover { color: var(--nd-text-display); }
`;

const Hero = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 20px;
  align-items: center;
  margin-bottom: 24px;
`;

const Avatar = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: ${({ src }) =>
    src ? `url(${src}) center/cover no-repeat` : "var(--nd-surface)"};
  border: 1px solid var(--nd-border-visible);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Doto', 'Space Mono', monospace;
  font-size: 28px;
  color: var(--nd-text-display);
  image-rendering: pixelated;
`;

const Name = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: var(--nd-text-display);
  margin: 0;
  line-height: 1.2;
`;

const Email = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.04em;
  color: var(--nd-text-secondary);
  margin: 4px 0 0;
`;

const Strip = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  border: 1px solid var(--nd-border);
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 24px;
`;

const Cell = styled.div`
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-right: 1px solid var(--nd-border);
  border-bottom: 1px solid var(--nd-border);

  &:nth-child(2) { border-right: 0; }
  &:nth-child(3),
  &:nth-child(4) { border-bottom: 0; }
  &:last-child { border-right: 0; }
`;

const Label = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 9px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
`;

const Value = styled.span`
  font-family: 'Doto', 'Space Mono', monospace;
  font-size: 28px;
  font-weight: 700;
  color: var(--nd-text-display);
  line-height: 1;
`;

const Phrase = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 16px;
  font-style: italic;
  color: var(--nd-text-primary);
  margin: 0 0 16px;
  line-height: 1.4;
`;

const ProfileLink = styled.a`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-display);
  border-bottom: 1px solid var(--nd-border-visible);
  text-decoration: none !important;
  display: inline-block;
  padding-bottom: 2px;
`;

const NextKicker = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  margin: 24px 0 8px;
`;

const NextCard = styled.a`
  display: block;
  border: 1px solid var(--nd-border-visible);
  border-radius: 8px;
  padding: 16px;
  text-decoration: none !important;
  color: inherit;
  transition: border-color 200ms cubic-bezier(0.25, 0.1, 0.25, 1);
  margin-bottom: 24px;

  &:hover { border-color: var(--nd-text-display); }
`;

const NextTitle = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: var(--nd-text-display);
  margin: 0 0 4px;
  line-height: 1.25;
`;

const NextWhy = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 13px;
  color: var(--nd-text-secondary);
  margin: 0 0 8px;
  line-height: 1.4;
`;

const NextCta = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-display);
`;

const initialFor = (name, email) => {
  const src = (name || email || "?").trim();
  return src.slice(0, 1).toUpperCase();
};

const StatsModal = () => {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const { data } = useUserProgress(user && user.uid);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onOpen = () => setOpen(true);
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("gami:open-stats", onOpen);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("gami:open-stats", onOpen);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  if (!open || !user) return null;
  const xp = (data && data.xp) || 0;
  const level = (data && data.level) || 0;
  const streak = (data && data.streakDays) || 0;
  const lives = data && typeof data.lives === "number" ? data.lives : 5;
  const name = user.displayName || (user.email && user.email.split("@")[0]) || "Engineer";
  const phrase = pickPhrase(user.uid);
  const badgeIds = (data && data.badgeIds) || [];
  const next = recommendNext(badgeIds);
  const nextCta = next.kind === "labs" ? "Open labs →" : "Open course →";

  return (
    <Overlay onClick={() => setOpen(false)} role="dialog" aria-modal="true">
      <Panel onClick={(e) => e.stopPropagation()}>
        <Head>
          <Kicker>YOU · TODAY</Kicker>
          <CloseBtn type="button" onClick={() => setOpen(false)}>Close</CloseBtn>
        </Head>
        <Hero>
          <Avatar src={user.photoURL}>
            {!user.photoURL && initialFor(name, user.email)}
          </Avatar>
          <div>
            <Name>{name}</Name>
            <Email>{user.email}</Email>
          </div>
        </Hero>
        <Strip>
          <Cell><Label>XP</Label><Value>{xp}</Value></Cell>
          <Cell><Label>Level</Label><Value>{level}</Value></Cell>
          <Cell><Label>Streak</Label><Value>{streak}</Value></Cell>
          <Cell><Label>Lives</Label><Value>{lives}</Value></Cell>
        </Strip>
        <Phrase>"{phrase}"</Phrase>

        <NextKicker>What's next</NextKicker>
        <NextCard href={next.href}>
          <NextTitle>{next.title}</NextTitle>
          <NextWhy>{next.why}.</NextWhy>
          <NextCta>{nextCta}</NextCta>
        </NextCard>

        <ProfileLink href="/profile">Full profile →</ProfileLink>
      </Panel>
    </Overlay>
  );
};

export default StatsModal;
